/* Updated with white background theme - #02857E green, #F7CF0D yellow accents */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { LoginModal } from '@/components/auth/LoginModal';
import { SignupModal } from '@/components/auth/SignupModal';
import { usePageMeta } from '@/hooks/usePageMeta';
import LandingHeader from '@/components/landing/LandingHeader';

import lanerunnerLogoBlack from '@/assets/lanerunner-logo-black.png';
import lanerunnerLogoFooter from '@/assets/lanerunner-logo-footer.png.asset.json';
const lanerunnerLogoWhite = lanerunnerLogoFooter.url;
import dashboardMockup from '@/assets/dashboard-mockup.jpg';
import deviceMockup from '@/assets/lanerunner-device-mockup.png';
import lanerunnerMarkColor from '@/assets/lanerunner-mark-color.png';
import heroDashboard from '@/assets/hero-dashboard-screenshot.png';
import efficiencyIllustration from '@/assets/efficiency-illustration.png';
import postLoadsIllustration from '@/assets/lanerunner-board.png';
import searchLoadsIllustration from '@/assets/logistics-map-3d.jpg';
import postTrucksIllustration from '@/assets/logistics-map-3d.jpg';
import searchTrucksIllustration from '@/assets/logistics-map-3d.jpg';
import arrowsUpBg from '@/assets/arrows-up-background.png';
import arrowsDownBg from '@/assets/arrows-down-background.png';
import lanerunnerTextLogo from '@/assets/lanerunner-text-logo.png';
import lanerunnerWordmarkCyan from '@/assets/lanerunner-wordmark-cyan.png';
import atlasMascot from '@/assets/atlas-mascot.png';



import { Search, Quote, MapPin, TrendingUp, TrendingDown, DollarSign, BarChart3, Zap, CheckCircle, Star, Facebook, Instagram, Linkedin, Twitter, ArrowRight, ArrowUp, Shield, Users, X, Check, Upload, Truck, MessageSquare, Map, Video, Sparkles, Bell, Target, Globe, HelpCircle, Handshake, ChevronRight, CreditCard, LayoutDashboard, Flame, Snowflake, MapPinned, Headphones, Package, Building2, FlaskConical, Gift } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import AtlasTypewriter from '@/components/landing/load-board/AtlasTypewriter';
// PricingCardEnhanced removed — replaced by inline 5-plan preview
import CostSavingsDialog from '@/components/landing/load-board/CostSavingsDialog';
import { LiveLoadsTicker } from '@/components/landing/load-board/LiveLoadsTicker';
import HeroRotator from '@/components/landing/load-board/HeroRotator';

const LoadBoardLandingPage = () => {
  usePageMeta({
    title: 'Load Board',
    description: 'Premium freight load board platform connecting carriers and brokers. Find verified loads, negotiate transparent rates, and book freight instantly with real-time tracking.'
  });
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isCostSavingsOpen, setIsCostSavingsOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [canadaTooltipOpen, setCanadaTooltipOpen] = useState(false);
  const canadaTooltipTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCanadaClick = () => {
    setCanadaTooltipOpen(true);
    if (canadaTooltipTimer.current) clearTimeout(canadaTooltipTimer.current);
    canadaTooltipTimer.current = setTimeout(() => setCanadaTooltipOpen(false), 3000);
  };

  // Handle scroll to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const faqItems = [{
    question: "How is LaneRunner different from other load boards?",
    answer: "LaneRunner is built on transparency. Unlike traditional load boards that hide broker margins and provide stale data, we show real-time rates, verified carrier/broker information, and enable direct communication. Our ATLAS AI provides market intelligence that helps you make informed decisions."
  }, {
    question: "How does FMCSA verification work?",
    answer: "When you sign up, we verify your MC/DOT number directly with the FMCSA database. This ensures every carrier and broker on our platform is legitimate, has active authority, and meets safety requirements. Verification typically takes just a few minutes."
  }, {
    question: "What does 'Early Access - FREE' include?",
    answer: "During our early access period, you get full access to all LaneRunner features at no cost: unlimited load searches and postings, ATLAS AI rate recommendations, direct chat and video, FMCSA verification, and real-time market data. Founding members will keep special benefits even after we introduce paid plans."
  }, {
    question: "How does ATLAS AI help with rate negotiations?",
    answer: "ATLAS analyzes millions of historical loads to provide real-time rate predictions. It shows you the market average, predicts rate trends, and suggests optimal booking windows. Carriers use it to find the best-paying loads, while brokers use it to price competitively."
  }, {
    question: "Can I connect directly with carriers/brokers?",
    answer: "Yes! LaneRunner includes built-in chat and video calling. You can verify equipment, discuss load details, and negotiate rates directly without phone tag or endless email chains. Every user's identity and authority is verified, so you know exactly who you're working with."
  }, {
    question: "What equipment types are supported?",
    answer: "We currently support Van, Reefer, and Flatbed with full features. Step Deck, Lowboy, Tanker, and other specialty equipment types are coming soon. All equipment types will have the same transparency features and ATLAS AI support."
  }];


  // Hero entrance animation
  useEffect(() => {
    const heroTimer = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(heroTimer);
  }, []);

  return <div className="min-h-screen bg-white">
      {/* Navigation */}
      <LandingHeader />

      {/* SECTION 1: Hero */}
      <section className="relative min-h-screen flex flex-col overflow-hidden bg-white">
        <img
          src={heroDashboard}
          alt=""
          className="absolute inset-0 w-full h-full object-cover z-0" style={{ opacity: 0.12 }}
        />
        {/* Split Layout Container */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-12 sm:pb-16 flex-grow flex items-center w-full">
          <div className={`w-full transition-all duration-1000 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <HeroRotator />
          </div>
        </div>

        {/* Float animation keyframes */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </section>


      {/* SECTION: AI Assisted Freight Banner */}

      <section className="relative py-12 sm:py-16 overflow-hidden bg-white">
        {/* Live loads ticker */}
        <LiveLoadsTicker />


      </section>

      {/* CREDIBILITY STRIP */}
      <section className="relative py-8 sm:py-10 overflow-hidden bg-gray-50 border-y border-gray-100">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-xl sm:text-2xl lg:text-3xl text-gray-900 mb-2">
            <span className="text-[#1ab7aa]">45,000+</span> Loads Available. <span className="text-[#1ab7aa]">12,000+</span> Carriers.
          </h2>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto mb-6 sm:mb-8">
            Real freight. Real brokers. Real carriers. You're not starting from zero.
          </p>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Choose Your Role</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
            <Link to="/brokers" className="group relative bg-[#1ab7aa] rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left">
              <div className="absolute -right-3 -bottom-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <Building2 className="w-20 h-20 sm:w-24 sm:h-24 text-white" />
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-xs sm:text-sm text-white font-semibold mb-1">Brokers</div>
              <div className="text-xs text-white/70 leading-relaxed">Post loads, manage carriers, and move freight faster.</div>
            </Link>

            <Link to="/carriers" className="group relative bg-[#1ab7aa] rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left">
              <div className="absolute -right-3 -bottom-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <Truck className="w-20 h-20 sm:w-24 sm:h-24 text-white" />
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-xs sm:text-sm text-white font-semibold mb-1">Carriers</div>
              <div className="text-xs text-white/70 leading-relaxed">Find loads, post trucks, and grow your business.</div>
            </Link>

            <Link to="/shippers" className="group relative bg-[#1ab7aa] rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left">
              <div className="absolute -right-3 -bottom-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <Package className="w-20 h-20 sm:w-24 sm:h-24 text-white" />
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-xs sm:text-sm text-white font-semibold mb-1">Shippers</div>
              <div className="text-xs text-white/70 leading-relaxed">Post your freight to the open board and get covered fast.</div>
            </Link>

            <div className="group relative bg-[#1ab7aa] rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left">
              <div className="absolute -right-3 -bottom-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <Users className="w-20 h-20 sm:w-24 sm:h-24 text-white" />
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-xs sm:text-sm text-white font-semibold mb-1">Broker/Carrier</div>
              <div className="text-xs text-white/70 leading-relaxed mb-2">One account for both sides of the freight market.</div>
              <div className="flex gap-2">
                <Link to="/brokers" className="text-xs font-medium text-white/90 hover:text-white hover:underline">Brokers →</Link>
                <Link to="/carriers" className="text-xs font-medium text-white/90 hover:text-white hover:underline">Carriers →</Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION: Are Middlemen Costing You? */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden bg-white">
        {/* Faint Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <img src={lanerunnerMarkColor} alt="" className="w-[400px] sm:w-[500px] lg:w-[600px] h-auto opacity-[0.05]" aria-hidden="true" />
        </div>
        <div className="relative max-w-6xl mx-auto">
          
          {/* First Row: 50/50 Split */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start mb-16 sm:mb-24">
            
            {/* Left Column: Video */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200">
                <img src="/images/dashboard-preview.png" alt="LANERUNNER Dashboard" className="w-full h-auto" />
              </div>
            </div>
            
            {/* Right Column: Content */}
            <div className="lg:pt-4">
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-sm font-medium text-gray-400">01</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl text-gray-900">
                  Is Your Load Board Still Worth $250 a Month?
                </h2>
              </div>
              
              <ul className="space-y-4 mb-8">
                {["Paying $250+ for a platform that hasn't meaningfully improved", "Blind booking trucks without real visibility or confidence", "Constant back-and-forth just to figure out what's actually available", "No real assistance — just more manual work and guesswork"].map((item, index) => <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="w-5 h-5 text-[#1ab7aa] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>)}
              </ul>
              
              <p className="text-gray-600 leading-relaxed">
                LaneRunner keeps the workflow familiar — but adds the speed, visibility, and intelligence modern freight actually needs.
              </p>
            </div>
            
          </div>
          
          {/* Second Row: Smarter Tools, Faster Work */}
          <div className="border-t border-gray-100 pt-12 sm:pt-16">
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-sm font-medium text-gray-400">02</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-gray-900">
                Smarter Tools, Faster Work
              </h2>
            </div>
            
            <p className="text-gray-600 leading-relaxed max-w-3xl mb-4">
              LaneRunner cuts out the busywork that slows you down on old-school load boards. With clean navigation, instant interactions, and AI assisting behind the scenes, you move through your day faster and with less effort.
            </p>
            <p className="text-gray-600 leading-relaxed max-w-3xl">
              No complicated menus. No guesswork. No wasted motion — just a modern load board that works the way it should.
            </p>
          </div>
          
        </div>
      </section>

      {/* SECTION: 03/04 - Reversed Layout */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden bg-white">
        <div className="relative max-w-6xl mx-auto">
          
          {/* Section 03: Unlock Efficiency with LaneRunner */}
          <div className="relative pt-0">
            <div className="flex items-baseline gap-3 mb-10">
              <span className="text-sm font-medium text-gray-400">03</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-gray-900">
                Do the Same Work — Faster, Cleaner, Smarter
              </h2>
            </div>
            
            {/* Row 1: Card + Large Text + Card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {/* Card 1: Post Loads Effortlessly */}
              <div className="flex flex-col">
                <h3 className="text-xl sm:text-2xl text-gray-900 mb-2">01. Post Loads Effortlessly</h3>
                <p className="text-gray-600 text-sm sm:text-base">Create and post loads in seconds with clean workflows, smart suggestions, and fewer mistakes — so you spend less time fixing details and more time moving freight.</p>
              </div>
              
              {/* Decorative Text: The Core Four with Arrow Graphics */}
              <div className="relative flex items-center justify-center">
                {/* Arrow Graphics SVG Overlay - Hidden on mobile */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none hidden md:block" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#1ab7aa" opacity="0.3" />
                    </marker>
                  </defs>
                  <path d="M -80 100 Q -20 100 30 100" stroke="#1ab7aa" strokeWidth="2" fill="none" opacity="0.25" markerEnd="url(#arrowhead)" strokeLinecap="round" strokeDasharray="150" strokeDashoffset="150" className="animate-[drawArrow_2s_ease-out_0.5s_forwards]" />
                  <path d="M 290 100 Q 240 100 190 100" stroke="#1ab7aa" strokeWidth="2" fill="none" opacity="0.25" markerEnd="url(#arrowhead)" strokeLinecap="round" strokeDasharray="150" strokeDashoffset="150" className="animate-[drawArrow_2s_ease-out_0.8s_forwards]" />
                  <path d="M 40 340 Q 100 340 160 340" stroke="#1ab7aa" strokeWidth="2" fill="none" opacity="0.25" markerEnd="url(#arrowhead)" strokeLinecap="round" strokeDasharray="150" strokeDashoffset="150" className="animate-[drawArrow_2s_ease-out_1.1s_forwards]" />
                </svg>
                <p className="text-4xl sm:text-5xl lg:text-6xl italic text-gray-400 leading-tight text-center relative z-10">
                  Core<br />Workflows
                </p>
              </div>
              
              {/* Card 2: Search Loads With Precision */}
              <div className="flex flex-col">
                <h3 className="text-xl sm:text-2xl text-gray-900 mb-2 whitespace-nowrap">02. Search Loads With Precision</h3>
                <p className="text-gray-600 text-sm sm:text-base">Find the right freight faster with filters that actually work — clear rates, real data, and lane insights that help you make decisions quickly.</p>
              </div>
            </div>
            
            {/* Row 2: 2 Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="flex flex-col items-center">
                <h3 className="text-xl sm:text-2xl text-gray-900 mb-2">03. Post Trucks Instantly</h3>
                <p className="text-gray-600 text-sm sm:text-base text-center">Get your capacity in front of the right people without extra steps, delays, or clutter — and get matched faster.</p>
              </div>
              <div className="flex flex-col items-center">
                <h3 className="text-xl sm:text-2xl text-gray-900 mb-2">04. Search Trucks Smarter</h3>
                <p className="text-gray-600 text-sm sm:text-base text-center">Identify reliable capacity quickly with cleaner profiles, better visibility, and tools designed to reduce guesswork.</p>
              </div>
            </div>
          </div>
          
        </div>
      </section>

      {/* White Paper Section */}
      <section className="relative py-16 sm:py-20 lg:py-28 overflow-hidden">
        {/* Base gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#f0f7f6] via-[#f5f7fa] to-[#f8faf5]" />
        
        {/* Box grid pattern */}
        <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: 'linear-gradient(#1ab7aa 1px, transparent 1px), linear-gradient(90deg, #1ab7aa 1px, transparent 1px)',
        backgroundSize: '48px 48px'
      }} />
        
        {/* Floating orbs for ambient color */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#1ab7aa]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#c4e84a]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-[#1ab7aa]/5 rounded-full blur-2xl" />
        
        {/* Right side flowing curves - enhanced */}
        <div className="absolute right-0 top-0 bottom-0 pointer-events-none w-1/3">
          <svg className="w-full h-full" viewBox="0 0 300 600" fill="none" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#c4e84a" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#1ab7aa" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            <path d="M100 0 Q300 200 100 400 Q-100 600 100 800" stroke="url(#lineGradient1)" strokeWidth="2" fill="none" />
            <path d="M150 0 Q350 250 150 450 Q-50 650 150 850" stroke="url(#lineGradient1)" strokeWidth="1.5" fill="none" opacity="0.7" />
            <path d="M200 0 Q400 300 200 500 Q0 700 200 900" stroke="url(#lineGradient1)" strokeWidth="1" fill="none" opacity="0.5" />
          </svg>
        </div>
        
        {/* Left side decorative elements */}
        <div className="absolute left-0 top-0 bottom-0 pointer-events-none w-1/4">
          <svg className="w-full h-full" viewBox="0 0 200 600" fill="none" preserveAspectRatio="none">
            <circle cx="30" cy="100" r="4" fill="#1ab7aa" opacity="0.3" />
            <circle cx="60" cy="200" r="3" fill="#c4e84a" opacity="0.4" />
            <circle cx="20" cy="350" r="5" fill="#1ab7aa" opacity="0.2" />
            <circle cx="80" cy="450" r="3" fill="#c4e84a" opacity="0.3" />
            <circle cx="40" cy="550" r="4" fill="#1ab7aa" opacity="0.25" />
          </svg>
        </div>
        
        {/* Floating geometric shapes */}
        <div className="absolute top-16 right-1/4 w-16 h-16 border border-[#1ab7aa]/20 rounded-xl rotate-12 hidden lg:block" />
        <div className="absolute bottom-24 left-1/4 w-12 h-12 border border-[#c4e84a]/30 rounded-lg -rotate-6 hidden lg:block" />
        <div className="absolute top-1/3 right-16 w-8 h-8 bg-[#1ab7aa]/10 rounded-full hidden lg:block" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left: ATLAS AI Assistant Logo with typewriter Q&A */}
            <div className="relative hidden lg:flex flex-col justify-center items-center">
              <AtlasTypewriter>
                {/* ATLAS Logo with glow effects */}
                <div className="relative flex justify-center items-center">
                  {/* Outer pulsing glow layer */}
                  <div className="absolute inset-0 bg-[#1ab7aa]/15 rounded-full blur-3xl animate-[pulseGlow_3s_ease-in-out_infinite]" />
                  
                  {/* Middle glow layer */}
                  <div className="absolute inset-8 bg-[#1ab7aa]/25 rounded-full blur-2xl" />
                  
                  {/* Inner glow layer */}
                  <div className="absolute inset-16 bg-[#1ab7aa]/30 rounded-full blur-xl" />
                  
                  {/* ATLAS Logo with floating animation */}
                  <img src={atlasMascot} alt="ATLAS - LaneRunner's AI Freight Assistant" className="relative z-10 w-80 h-80 object-contain animate-[float_120s_ease-in-out_infinite] drop-shadow-[0_20px_50px_rgba(26,183,170,0.4)] hover:scale-105 transition-transform duration-300" />
                </div>
              </AtlasTypewriter>
            </div>
            
            {/* Right: Text content - centered on mobile/tablet, left-aligned next to graphic on desktop */}
            <div className="flex flex-col items-center text-center lg:items-start lg:text-left lg:col-span-1">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#1a3a5c] font-semibold mb-4 leading-tight">
                ATLAS — Your Built-In Freight Assistant
              </h2>
              <p className="text-lg sm:text-xl text-[#1ab7aa] font-semibold mb-4">
                Get answers, insights, and help instantly — without stopping your workflow.
              </p>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 max-w-lg">
                LaneRunner is built to move fast — and ATLAS makes it even faster.
              </p>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 max-w-lg">
                Ask questions, get lane insights, check conditions, or understand what to do next — all without digging through menus or guessing your next step.
              </p>
              <p className="text-sm sm:text-base text-gray-700 font-medium italic mb-6 max-w-lg">
                No onboarding calls. No waiting for support. No wasted time.
              </p>
              <Link to="/pricing">
                <button className="relative overflow-hidden bg-gradient-to-r from-[#1ab7aa] to-[#14a89b] hover:from-[#159b90] hover:to-[#129085] text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-[#1ab7aa]/25 group">
                  <span className="relative z-10">Try ATLAS</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: Transparency Features */}
      <section className="relative py-12 sm:py-16 lg:py-24 overflow-hidden bg-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-sm text-gray-400 uppercase tracking-widest mb-3">See LaneRunner in Action</p>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl text-gray-900 mb-4">
              See Everything. <span className="text-[#1ab7aa]">Decide Faster.</span>
            </h2>
          </div>

          {/* Feature 1: Quick Posting — stacked layout for video */}
          <div className="mb-16 sm:mb-24 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-[#1ab7aa]/10 rounded-xl flex items-center justify-center">
                <Zap className="w-5 sm:w-6 h-5 sm:h-6 text-[#1ab7aa]" />
              </div>
              <h3 className="text-lg sm:text-2xl text-gray-900">Post Loads in Seconds</h3>
            </div>
            <p className="text-sm sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Voice post, quick form, or bulk upload — get freight live instantly. ATLAS helps fill in details so you move faster and stay accurate.
            </p>
            <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-xl border border-gray-200">
              <video
                autoPlay
                muted
                loop
                playsInline
                poster="/images/poster-post-loads.jpg"
                className="w-full h-auto"
              >
                <source src="/videos/LRposting.mp4" type="video/mp4" />
              </video>
            </div>
          </div>

          {/* Feature 2: Carrier Posts Truck → Instant Load Matches */}
          <div className="mb-16 sm:mb-24 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-lr-yellow/10 rounded-xl flex items-center justify-center">
                <Truck className="w-5 sm:w-6 h-5 sm:h-6 text-lr-yellow" />
              </div>
              <h3 className="text-lg sm:text-2xl text-gray-900">Post Trucks. Get Matched.</h3>
            </div>
            <p className="text-sm sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Share your availability in seconds and get in front of the right loads. Less waiting. More movement.
            </p>
            <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-xl border border-gray-200">
              <video autoPlay muted loop playsInline poster="/images/poster-post-truck.jpg" className="w-full h-auto">
                <source src="/videos/Truck_Posting.mp4" type="video/mp4" />
              </video>
            </div>
          </div>

          {/* Feature 3: Search Loads */}
          <div className="mb-16 sm:mb-24 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-[#1ab7aa]/10 rounded-xl flex items-center justify-center">
                <Search className="w-5 sm:w-6 h-5 sm:h-6 text-[#1ab7aa]" />
              </div>
              <h3 className="text-lg sm:text-2xl text-gray-900">Find Loads — Fast</h3>
            </div>
            <p className="text-sm sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Search with filters that actually work — clear rates, real data, and instant results. No clutter. No wasted clicks.
            </p>
            <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-xl border border-gray-200">
              <video autoPlay muted loop playsInline poster="/images/poster-search-loads.jpg" className="w-full h-auto">
                <source src="/videos/SearchLoads.mp4" type="video/mp4" />
              </video>
            </div>
          </div>

          {/* Feature 4: Freight Lab */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-lr-yellow/10 rounded-xl flex items-center justify-center">
                <FlaskConical className="w-5 sm:w-6 h-5 sm:h-6 text-lr-yellow" />
              </div>
              <h3 className="text-lg sm:text-2xl text-gray-900">Find Capacity You Can Trust</h3>
            </div>
            <p className="text-sm sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Search trucks with better visibility, cleaner profiles, and real context — so you spend less time guessing and more time booking.
            </p>
            <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-xl border border-gray-200">
              <video autoPlay muted loop playsInline poster="/images/poster-freight-lab.jpg" className="w-full h-auto">
                <source src="/videos/Lane_Analyzer.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: More Additional Features */}
      <section className="relative py-12 sm:py-16 lg:py-24 overflow-hidden bg-white">
        {/* Background Watermark Text - removed, now integrated with phone mockup */}

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl text-gray-950 font-bold">
              Your Fastest Decisions <span className="text-[#1ab7aa]">Start Here</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-500 mt-3">Ask ATLAS anything — just like this:</p>
          </div>

          {/* Feature Cards - Row 1: 3 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* Card 1: Rate Intelligence */}
            <div className="group flex items-start gap-4 bg-[#1ab7aa]/25 rounded-xl p-5 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-default">
              <div className="relative w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0">
                <div className="absolute inset-0 bg-[#1ab7aa]/50 rounded-lg transition-all duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:shadow-[4px_4px_0_rgba(26,183,170,0.25)]" />
                <div className="relative w-full h-full flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Know the Right Rate Instantly</h3>
                <p className="text-xs sm:text-sm text-gray-700"><p className="text-xs sm:text-sm text-gray-700">See real market-backed pricing instantly — so you don't underprice or miss opportunities.</p></p>
              </div>
            </div>

            {/* Card 2: Lane Analytics */}
            <div className="group flex items-start gap-4 bg-[#1ab7aa]/25 rounded-xl p-5 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-default">
              <div className="relative w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0">
                <div className="absolute inset-0 bg-[#1ab7aa]/50 rounded-lg transition-all duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:shadow-[4px_4px_0_rgba(26,183,170,0.25)]" />
                <div className="relative w-full h-full flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Understand the Lane Before You Book</h3>
                <p className="text-xs sm:text-sm text-gray-700"><p className="text-xs sm:text-sm text-gray-700">Know the risks, timing, and conditions before you commit — not after.</p></p>
              </div>
            </div>

            {/* Card 3: Smart Search */}
            <div className="group flex items-start gap-4 bg-[#1ab7aa]/25 rounded-xl p-5 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-default sm:col-span-2 lg:col-span-1">
              <div className="relative w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0">
                <div className="absolute inset-0 bg-[#1ab7aa]/50 rounded-lg transition-all duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:shadow-[4px_4px_0_rgba(26,183,170,0.25)]" />
                <div className="relative w-full h-full flex items-center justify-center">
                  <Search className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Find Exactly What You Need — Fast</h3>
                <p className="text-xs sm:text-sm text-gray-700"><p className="text-xs sm:text-sm text-gray-700">Search naturally and get instant results — no digging, no wasted time.</p></p>
              </div>
            </div>
          </div>

          {/* Feature Cards - Row 2: 2 columns centered */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto mb-8 sm:mb-12">
            {/* Card 4: Negotiation Guide */}
            <div className="group flex items-start gap-4 bg-[#1ab7aa]/25 rounded-xl p-5 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-default">
              <div className="relative w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0">
                <div className="absolute inset-0 bg-[#1ab7aa]/50 rounded-lg transition-all duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:shadow-[4px_4px_0_rgba(26,183,170,0.25)]" />
                <div className="relative w-full h-full flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Negotiate With Confidence</h3>
                <p className="text-xs sm:text-sm text-gray-700"><p className="text-xs sm:text-sm text-gray-700">Get real-time guidance so you don't leave money on the table.</p></p>
              </div>
            </div>

            {/* Card 5: Market Alerts */}
            <div className="group flex items-start gap-4 bg-[#1ab7aa]/25 rounded-xl p-5 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-default">
              <div className="relative w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0">
                <div className="absolute inset-0 bg-[#1ab7aa]/50 rounded-lg transition-all duration-300 group-hover:-translate-x-1 group-hover:-translate-y-1 group-hover:shadow-[4px_4px_0_rgba(26,183,170,0.25)]" />
                <div className="relative w-full h-full flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Stay Ahead of the Market</h3>
                <p className="text-xs sm:text-sm text-gray-700"><p className="text-xs sm:text-sm text-gray-700">See shifts as they happen — and act before everyone else.</p></p>
              </div>
            </div>
          </div>

          {/* Phone Mockup Placeholder with vertical ATLAS / AGENT text */}
          <div className="relative flex justify-center items-center gap-4 sm:gap-8 mt-8 sm:mt-12 mb-8 sm:mb-12">
            {/* Floating micro-captions */}
            <div className="absolute top-16 left-[10%] lg:left-[15%] text-[11px] text-gray-700 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm animate-float hidden sm:block">
              Instant rate insight
            </div>
            <div className="absolute top-1/2 right-[8%] lg:right-[12%] text-[11px] text-gray-700 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm animate-float-slow hidden sm:block">
              Live lane analysis
            </div>
            <div className="absolute bottom-24 left-[12%] lg:left-[18%] text-[11px] text-gray-700 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm animate-float hidden sm:block" style={{ animationDelay: '1s' }}>
              Real-time answers
            </div>

            {/* Left side - ATLAS vertical text */}
            <div className="h-[800px] flex items-center pointer-events-none select-none">
              <span className="text-[120px] sm:text-[140px] lg:text-[160px] font-bold text-gray-100/40 tracking-widest uppercase" style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              transform: 'rotate(180deg)'
            }}>
                ATLAS
              </span>
            </div>
            
            {/* Phone mockup with video */}
            <div className="flex flex-col items-center">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">Real Example</p>
              <div className="bg-black rounded-[2.5rem] border-8 border-gray-300 shadow-2xl overflow-hidden flex-shrink-0 relative" style={{
              width: '380px',
              height: '840px'
            }}>
                <video
                  src="/videos/LR_cell_app_1.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            {/* Right side - AGENT vertical text */}
            <div className="h-[800px] flex items-center pointer-events-none select-none">
              <span className="text-[120px] sm:text-[140px] lg:text-[160px] font-bold text-gray-100/40 tracking-widest uppercase" style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }}>
                AGENT
              </span>
            </div>
          </div>

        </div>
      </section>








      {/* SECTION 14: Testimonials */}
      <section className="relative py-16 sm:py-20 lg:py-32 overflow-hidden bg-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#1ab7aa]/10 border border-[#1ab7aa]/30 rounded-full mb-4 sm:mb-6">
              <Star className="w-4 h-4 text-lr-yellow fill-lr-yellow" />
              <span className="text-[#1ab7aa] text-xs sm:text-sm font-medium">Real Success Stories</span>
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl text-gray-900 mb-4 px-2">
              Used by Brokers, Carriers, and Dispatchers <span className="text-[#1ab7aa]">Every Day</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-500">See why carriers and brokers choose LaneRunner</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-16">
            {[{
            quote: "I used LaneRunner back when it was just freight rates, and now their load board release is a fantastic addition that keeps my freight moving with ease. Finally a platform that gets it — everything is transparent, fast, and built for how freight actually moves.",
            name: "Daniel V.",
            role: "Santel Produce",
            location: "Nogales, AZ",
            stat: "Instant rate clarity every time",
            initial: "D"
          }, {
            quote: "I love how simple this software is. Clean, uncluttered, and easy to work with. I can search rates, post loads, get calls, and use auto-bidding without fighting through a messy system. LaneRunner lets me focus on what I do best — moving freight.",
            name: "Jim W.",
            role: "R&R Express",
            location: "Long Island, NY",
            stat: "Hours saved every week",
            initial: "J"
          }, {
            quote: "I use LaneRunner to double-check rates and keep everything transparent. I've been in this business 45 years and never used a load board before. Now I post my freight on the board, the calls come in, and I can see what the market is really paying — super helpful. Thumbs up.",
            name: "Paul B.",
            role: "Agritrade Farms",
            location: "Miami, FL",
            stat: "Booking made simple",
            initial: "P"
          }].map((testimonial, index) => <div key={index} className="group bg-white border border-gray-200 rounded-2xl p-5 sm:p-8 hover:border-[#1ab7aa]/40 transition-all duration-500 shadow-sm hover:shadow-lg">
                <div className="flex mb-3 sm:mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 sm:w-5 h-4 sm:h-5 fill-lr-yellow text-lr-yellow" />)}
                </div>
                
                <blockquote className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-br from-[#1ab7aa] to-[#1ab7aa]/60 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base">
                    {testimonial.initial}
                  </div>
                  <div>
                    <div className="text-gray-900 text-sm sm:text-base">{testimonial.name}</div>
                    <div className="text-gray-500 text-xs sm:text-sm">{testimonial.role} • {testimonial.location}</div>
                  </div>
                </div>

                <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
                  <div className="text-[#1ab7aa] text-sm sm:text-base">{testimonial.stat}</div>
                </div>
              </div>)}
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            {["FMCSA Safety & Authority Checks", "Advanced Fraud Prevention System", "Encrypted, Secure Freight Transactions"].map((item, i) => <div key={i} className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                <Check className="w-4 h-4 text-[#1ab7aa] flex-shrink-0" />
                <span>{item}</span>
              </div>)}
          </div>
        </div>
      </section>


      {/* SECTION 16: Final CTA */}
      <section className="relative py-16 sm:py-20 lg:py-28 overflow-hidden bg-white">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl mb-4 sm:mb-6 px-2">
            <span className="text-gray-900">Ready to Move </span>
            <span className="text-[#1ab7aa]">Freight Smarter?</span>
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto mb-8 sm:mb-10 px-4">
            Start posting, finding, and moving freight with clarity — no guesswork, no hidden systems.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4 px-4">
            <Button asChild className="w-full sm:w-auto bg-[#1ab7aa] text-white font-bold px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg hover:bg-[#02857E]">
              <Link to="/pricing">Sign Up Now</Link>
            </Button>
          </div>

          <p className="text-gray-500 text-xs sm:text-sm mb-6 sm:mb-8">
            No contracts • Cancel anytime • FMCSA verification included
          </p>

          
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 sm:py-16 px-4 sm:px-6 border-t border-white/10" style={{
      backgroundColor: '#0A0E15'
    }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="col-span-2 md:col-span-1">
              <img src={lanerunnerLogoWhite} alt="LaneRunner" className="h-14 sm:h-16 mb-4" />
              <p className="text-gray-400 text-xs sm:text-sm">
                The transparent freight marketplace for carriers and brokers.
              </p>
            </div>

            <div>
              <h4 className="text-white mb-3 sm:mb-4 text-sm sm:text-base">Platform</h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li><a href="/shippers" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">For Shippers</a></li>
                <li><a href="/brokers" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">For Brokers</a></li>
                <li><a href="/carriers" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">For Carriers</a></li>
                <li><a href="/pricing" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">Pricing</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white mb-3 sm:mb-4 text-sm sm:text-base">Company</h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li><a href="/about" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">About Us</a></li>
                <li><a href="/investors" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">Investors</a></li>
                <li><a href="/affiliates" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">Affiliate Program</a></li>
                <li><a href="/privacy" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">Terms of Service</a></li>
                <li><a href="/cancellation-policy" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">Cancellation Policy</a></li>
                <li><a href="/cookies" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">Cookie Policy</a></li>
                <li><button type="button" onClick={() => window.dispatchEvent(new Event('lr-open-cookie-settings'))} className="text-gray-400 hover:text-[#1ab7aa] transition-colors text-left">Cookie Preferences</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white mb-3 sm:mb-4 text-sm sm:text-base">Connect</h4>
              <div className="flex gap-3 sm:gap-4">
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">
                  <Facebook className="w-4 sm:w-5 h-4 sm:h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">
                  <Instagram className="w-4 sm:w-5 h-4 sm:h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">
                  <Linkedin className="w-4 sm:w-5 h-4 sm:h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors">
                  <Twitter className="w-4 sm:w-5 h-4 sm:h-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 sm:pt-8 text-center text-gray-500 text-xs sm:text-sm">
            © 2026 LaneRunner. All rights reserved. Built for freight professionals who demand transparency.
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button onClick={scrollToTop} className={`fixed bottom-6 right-6 z-50 p-3 bg-[#1ab7aa] hover:bg-[#158f85] text-white rounded-full shadow-lg transition-all duration-300 ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`} aria-label="Scroll to top">
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* Cost Savings Dialog */}
      <CostSavingsDialog open={isCostSavingsOpen} onOpenChange={setIsCostSavingsOpen} />
    </div>;
};
export default LoadBoardLandingPage;